import React, { useContext, useEffect, useRef, useState } from 'react';
import { I18nContext } from '../../context/translations/TranslationsContext.context';
import { CONTRIBUTION_LIMITS } from '../../utils/contributionLimits';
import { fmtEur, fmtNum, estimateTaxSaving } from '../../utils/formatArcaNumbers';
import { projectMonthly } from '../../utils/monthlyProjection';
import { PlanReturnView, rateOf } from '../../utils/transferReturns';
import { periodLabel } from './projectionLegend';

const MIN_CONTRIBUTION = 0;
const STEP = 5;
// Tipo marginal de IRPF para estimar el ahorro fiscal. NO tenemos el salario del
// usuario (ni individuales ni autónomos), así que usamos un marginal representativo
// del salario más habitual en España (~21.000 €/año → tramo 24-30% → ~25%). La
// leyenda lo explica para que el usuario lo interprete; el cálculo fino vive en el
// simulador de IRPF. (Antes era 37%, propio de salarios B2B altos → sobreestimaba.)
const ESTIMATED_MARGINAL_RATE = 0.25;
// Fallback de horizonte si el padre no pasa `yearsToRetirement` (no debería pasar).
const DEFAULT_YEARS_TO_RETIREMENT = 30;

interface ContributionSliderProps {
	currentContribution?: number;
	/**
	 * Tope (€/mes) del slider. Por defecto el límite MENSUAL del plan individual
	 * (1.500 €/año ÷ 12 = 125 €/mes). Para autónomos el padre pasa su máximo legal
	 * real (del backend, `maxFreelanceContributionEndOfNextPeriod`), que es dinámico;
	 * no se re-deriva ese cap aquí.
	 */
	maxContribution?: number;
	totalSaved?: number;
	/**
	 * Rentabilidad de comparsa del plan del usuario (`/plan-returns`, chirigota#576).
	 * `undefined` mientras llega; `null` sin datos: entonces no se proyecta (nunca una tasa por
	 * defecto).
	 */
	planReturn?: PlanReturnView | null;
	/** Años hasta la jubilación (67 − edad). Compartido con el simulador. */
	yearsToRetirement?: number;
	nextContributionDate?: string;
	contributionDay?: number;
	onContributionChange?: (amount: number) => void;
	onConfigure?: (amount: number) => void;
	onChangeDayClick?: () => void;
	/**
	 * Abre el modal de aportación PUNTUAL (única), adicional a la mensual. Solo se
	 * pasa en la vista individual/autónomo (B2C sin empresa). Si no se pasa, el
	 * botón puntual no se renderiza y el CTA mensual mantiene el ancho completo.
	 */
	onOneTimeContribution?: () => void;
	/**
	 * Multi-cuenta (B2B + rol individual): la única user_epp_config pertenece al
	 * plan de EMPLEO, así que la aportación periódica de ESTE plan no es
	 * configurable todavía (llega con las configs por plan, R2 de la épica
	 * multi-cuenta). Oculta slider/CTA y muestra nota "próximamente".
	 */
	configDisabled?: boolean;
}

const ContributionSliderV2: React.FC<ContributionSliderProps> = ({
	currentContribution = 0,
	maxContribution = CONTRIBUTION_LIMITS.INDIVIDUAL_PLAN_MONTHLY,
	totalSaved,
	planReturn,
	yearsToRetirement,
	nextContributionDate,
	contributionDay,
	onContributionChange,
	onConfigure,
	onChangeDayClick,
	onOneTimeContribution,
	configDisabled = false
}) => {
	const {
		state: { translate }
	} = useContext(I18nContext);

	const [sliderValue, setSliderValue] = useState(currentContribution);
	const [saved, setSaved] = useState(false);
	const [savedVisible, setSavedVisible] = useState(false);
	const fadeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
	const fadeTimer2 = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Lo guardado puede cambiar fuera del slider (el modal del correo, la config que llega
	// después): el slider se pone en ello, para no enseñar el importe viejo ni ofrecer volver a
	// guardarlo (chirigota#630).
	useEffect(() => {
		setSliderValue(currentContribution);
	}, [currentContribution]);

	const hasChanged = sliderValue !== currentContribution;

	const effectiveYears = yearsToRetirement ?? DEFAULT_YEARS_TO_RETIREMENT;
	// Mes a mes con la tasa mensual neta de comparsa y la aportación a final de mes, como comparsa,
	// los funnels y el resto de gráficas. Años enteros.
	const formattedProjection = planReturn
		? fmtEur(
				Math.round(
					projectMonthly(
						totalSaved ?? 0,
						sliderValue,
						rateOf(planReturn),
						Math.max(0, Math.round(effectiveYears))
					)
				)
			)
		: null;

	// Ahorro fiscal estimado anual con un marginal representativo (ver constante). Se
	// recalcula con el slider para que el usuario vea el impacto al moverlo.
	const taxSaving = estimateTaxSaving(sliderValue, ESTIMATED_MARGINAL_RATE);

	// Porcentaje de relleno para que la barra se coloree (verde) a medida que se
	// mueve, igual que el Slider de los simuladores (slider-arca): track gris +
	// relleno verde + pulgar blanco con borde verde.
	const fillPct = Math.min(
		100,
		Math.max(0, ((sliderValue - MIN_CONTRIBUTION) / (maxContribution - MIN_CONTRIBUTION)) * 100)
	);

	const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = Number(e.target.value);
		setSliderValue(value);
		onContributionChange?.(value);
	};

	useEffect(() => {
		if (saved) {
			requestAnimationFrame(() => setSavedVisible(true));
			fadeTimer.current = setTimeout(() => {
				setSavedVisible(false);
				fadeTimer2.current = setTimeout(() => setSaved(false), 300);
			}, 2000);
		}
		return () => {
			if (fadeTimer.current) clearTimeout(fadeTimer.current);
			if (fadeTimer2.current) clearTimeout(fadeTimer2.current);
		};
	}, [saved]);

	// Solo se llega con cambios: el botón está deshabilitado mientras no cambie la aportación.
	const handleConfigure = () => {
		onConfigure?.(sliderValue);
		setSaved(true);
	};

	const projectionBlock = (
		<div className="space-y-1">
			<p className="text-base font-bold font-heading text-foreground">
				{translate('individual.dashboard.projection.title') || 'Proyección a los 67 años'}
			</p>
			<p className="text-xs text-muted-foreground">
				{translate('individual.dashboard.projection.whenRetired') || 'Cuando te jubiles tendrás*:'}
			</p>
			{formattedProjection !== null ? (
				<p className="text-3xl font-bold font-heading text-primary pt-1">{formattedProjection}</p>
			) : (
				<p
					className="text-sm text-muted-foreground pt-1"
					data-testid="slider-projection-unavailable"
				>
					{planReturn === undefined
						? translate('individual.dashboard.projection.loading')
						: translate('individual.dashboard.projection.noData')}
				</p>
			)}
			{sliderValue > 0 && (
				<div className="pt-2 space-y-0.5" data-testid="slider-tax-saving">
					<div className="flex items-baseline justify-between">
						<span className="text-xs text-muted-foreground">
							{translate('benefit.simulator.estimatedTaxSavings') || 'Ahorro fiscal estimado'}
						</span>
						<span className="text-sm font-semibold text-primary">
							~{fmtNum(taxSaving, 0)}€ {translate('benefit.simulator.perYear') || '/año'}
						</span>
					</div>
					<p className="text-[11px] text-muted-foreground leading-snug">
						{(
							translate('individual.dashboard.projection.taxSavingNote') ||
							'Estimado con un tipo marginal del {rate}% (salario más habitual en España); tu ahorro real depende de tus ingresos.'
						).replace('{rate}', String(Math.round(ESTIMATED_MARGINAL_RATE * 100)))}
					</p>
				</div>
			)}
			{planReturn && (
				<p className="text-xs text-muted-foreground pt-2 leading-relaxed">
					*
					{translate('individual.dashboard.projection.disclaimer').replace(
						'{period}',
						periodLabel(translate, planReturn.periodYears)
					)}
				</p>
			)}
		</div>
	);

	if (configDisabled) {
		return (
			<div className="surface-card p-6 space-y-5">
				<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
					{translate('individual.dashboard.slider.title') || 'Tu aportación mensual'}
				</p>
				<p
					data-testid="slider-config-coming-soon"
					className="text-sm text-muted-foreground leading-relaxed"
				>
					{translate('individual.dashboard.slider.configComingSoon') ||
						'La aportación periódica para este plan estará disponible próximamente.'}
				</p>
				<div className="border-t border-border" />
				{projectionBlock}
			</div>
		);
	}

	return (
		<div className="surface-card p-6 space-y-5">
			{/* Label */}
			<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
				{translate('individual.dashboard.slider.title') || 'Tu aportación mensual'}
			</p>

			{/* Amount display */}
			<div className="flex items-baseline gap-1" data-testid="slider-amount-display">
				<span
					className="text-4xl font-bold font-heading text-foreground"
					data-testid="slider-current-contribution"
				>
					{sliderValue}
				</span>
				<span className="text-2xl font-bold font-heading text-foreground">€</span>
				<span className="text-sm text-muted-foreground ml-0.5">
					{translate('individual.dashboard.slider.perMonth') || '/mes'}
				</span>
			</div>

			{/* Slider */}
			<div>
				<input
					type="range"
					min={MIN_CONTRIBUTION}
					max={maxContribution}
					step={STEP}
					value={sliderValue}
					onChange={handleSliderChange}
					style={{
						background: `linear-gradient(to right, var(--primary) ${fillPct}%, var(--muted) ${fillPct}%)`
					}}
					className="w-full h-2 rounded-full appearance-none cursor-pointer
						[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-[1.1rem] [&::-webkit-slider-thumb]:h-[1.1rem]
						[&::-webkit-slider-thumb]:bg-background [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-primary
						[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-webkit-slider-thumb]:shadow-md
						[&::-moz-range-thumb]:w-[1.1rem] [&::-moz-range-thumb]:h-[1.1rem] [&::-moz-range-thumb]:bg-background
						[&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-primary [&::-moz-range-thumb]:rounded-full
						[&::-moz-range-thumb]:cursor-pointer"
				/>
				<div className="flex justify-between mt-1 text-[10px] text-muted-foreground">
					<span>{MIN_CONTRIBUTION}€</span>
					<span>{maxContribution}€</span>
				</div>
			</div>

			{/* Next contribution date + change day link */}
			{nextContributionDate && currentContribution > 0 && (
				<p className="text-sm -mt-2" data-testid="slider-next-contribution">
					<span className="text-foreground/60">
						{translate('individual.dashboard.slider.nextContribution') || 'Próxima aportación'}:
					</span>{' '}
					<span className="font-medium text-foreground" data-testid="slider-next-contribution-prop">
						{nextContributionDate}
					</span>
					{onChangeDayClick && (
						<>
							{' · '}
							<button
								type="button"
								onClick={onChangeDayClick}
								className="text-primary underline-offset-2 hover:underline"
							>
								{translate('individual.dashboard.slider.changeDay') || 'Cambiar día'}
							</button>
						</>
					)}
				</p>
			)}
			{!nextContributionDate && (
				<span data-testid="slider-next-contribution-prop" className="hidden" />
			)}

			{/* Confirm button / saved confirmation — same space, no layout shift.
			    El CTA mensual y el de aportación puntual conviven en la misma fila
			    (apilados en móvil) para que se entienda que se puede aportar de las
			    dos formas. El puntual solo aparece si el padre pasa el handler. */}
			<div className="flex flex-col sm:flex-row gap-3">
				{saved ? (
					<div
						data-testid="configure-contribution-btn"
						className={`flex-1 py-3 rounded-xl bg-primary/10 text-primary text-base font-heading font-medium text-center transition-opacity duration-300 ${savedVisible ? 'opacity-100' : 'opacity-0'}`}
					>
						✓ Aportación actualizada
					</div>
				) : (
					<button
						disabled={!hasChanged}
						onClick={handleConfigure}
						data-testid="configure-contribution-btn"
						className="flex-1 font-heading font-medium text-base py-3 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed bg-primary text-primary-foreground hover:bg-primary/90 disabled:hover:bg-primary"
					>
						{hasChanged
							? (
									translate('individual.dashboard.slider.changeButton') || 'Cambiar a {amount}€/mes'
								).replace('{amount}', String(sliderValue))
							: (
									translate('individual.dashboard.slider.currentButton') || 'Aportas {amount}€/mes'
								).replace('{amount}', String(sliderValue))}
					</button>
				)}
				{onOneTimeContribution && (
					<button
						type="button"
						onClick={onOneTimeContribution}
						data-testid="one-time-contribution-btn"
						className="flex-1 font-heading font-medium text-base py-3 rounded-xl border border-primary text-primary bg-transparent transition-colors hover:bg-primary/5"
					>
						{translate('individual.dashboard.slider.oneTimeButton') || 'Aportación puntual'}
					</button>
				)}
			</div>

			{/* Divider */}
			<div className="border-t border-border" />

			{/* Projection */}
			{projectionBlock}
		</div>
	);
};

export default ContributionSliderV2;
